import { Global } from '@emotion/react';
import styled from '@emotion/styled';
import { FC, FunctionComponent, ReactNode, SVGProps, useCallback, useState } from 'react';
import { Modal as BootstrapModal } from 'react-bootstrap';
import { ReactComponent as DeprecatedCloseIcon } from '../../assets/icons/DeprecatedCloseIcon.svg';
import { isFunction } from '../../helpers';
import { theme } from '../../theme';
import { HeadingMedium } from '../Typography';
import { CleanButton } from '../buttons';
import { CancelButton, SubmitButton } from './Buttons';
import { ModalButton } from './types';

type ModalAlign = 'left' | 'center' | 'right';

type ModalProps = {
  title?: string;
  titleStyle?: React.CSSProperties;
  text?: string | ReactNode;
  onClose?: () => void;
  onSubmit?: (() => void) | (() => Promise<void>);
  onCancel?: () => void;
  content?: ReactNode;
  icon?: FunctionComponent<SVGProps<SVGSVGElement>>;
  buttons?: {
    cancel?: ModalButton;
    submit?: ModalButton;
  };
  footerContent?: ReactNode;
  verticalButtons?: boolean;
  ButtonsContainerStyle?: React.CSSProperties;
  onHide: () => void;
  show: boolean;
  hideAfterSubmit?: boolean;
  width?: number | string;
  maxHeight?: string | number;
  hideCloseButton?: boolean;
  headerAlign?: ModalAlign;
  footerAlign?: ModalAlign;
  dataCy?: string;
  hideScroll?: boolean;
};
export const Modal: FC<ModalProps> = ({
  title,
  titleStyle,
  text,
  onClose,
  onSubmit,
  onCancel,
  content,
  buttons,
  footerContent,
  verticalButtons = false,
  ButtonsContainerStyle,
  onHide,
  show,
  width,
  maxHeight,
  dataCy,
  hideAfterSubmit = true,
  hideCloseButton = false,
  headerAlign = 'left',
  footerAlign = 'left',
  icon,
  hideScroll = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const onHideFunction = useCallback(() => {
    if (isFunction(onClose)) {
      onClose();
    }
    onHide();
  }, [onHide, onClose]);

  const onSubmitFunction = useCallback(async () => {
    try {
      if (isFunction(onSubmit)) {
        setIsLoading(true);
        // If the onSubmit function returns a Promise, wait for the result.
        await Promise.resolve(onSubmit());
        setIsLoading(false);
      }
      if (hideAfterSubmit) {
        onHide();
      }
    } catch (e) {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onHide, onSubmit]);

  const onCancelFunction = useCallback(() => {
    if (isFunction(onCancel)) {
      onCancel();
    }
    onHideFunction();
  }, [onHideFunction, onCancel]);

  maxHeight = formatCssSizeValue(maxHeight, '80vh');
  width = formatCssSizeValue(width, '450px');

  return (
    <BootstrapModal show={show} onHide={onHideFunction} data-cy={dataCy}>
      {width && <Global styles={{ '.modal-dialog': { maxWidth: `${width} !important` } }} />}
      <Container width={width} maxHeight={maxHeight}>
        {!hideCloseButton && (
          <CloseContainer>
            <CleanButton onClick={onHideFunction} style={{ textDecoration: 'none' }}>
              <DeprecatedCloseIcon role="img" aria-label="close icon" />
            </CleanButton>
          </CloseContainer>
        )}
        {icon && (
          <IconContainer>
            {(() => {
              const Icon = icon;
              return <Icon />;
            })()}
          </IconContainer>
        )}

        <HeaderText align={headerAlign}>
          <HeadingMedium style={titleStyle}>{title}</HeadingMedium>
          {text && <p>{text}</p>}
        </HeaderText>
        <Body style={{ overflowY: hideScroll ? 'hidden' : 'auto' }}>{content}</Body>
        {(buttons || footerContent) && (
          <ButtonsContainer style={ButtonsContainerStyle}>
            <FooterRow>
              {buttons && (
                <Buttons vertical={verticalButtons} align={footerAlign}>
                  <SubmitButton
                    onClick={onSubmitFunction}
                    button={buttons.submit && { ...buttons.submit, disabled: buttons.submit?.disabled || isLoading }}
                  />
                  <CancelButton
                    onClick={onCancelFunction}
                    button={buttons.cancel && { ...buttons.cancel, disabled: buttons.cancel?.disabled || isLoading }}
                  />
                </Buttons>
              )}
              {footerContent}
            </FooterRow>
          </ButtonsContainer>
        )}
      </Container>
    </BootstrapModal>
  );
};

const Container = styled.div<{ width: string | undefined; maxHeight: string | undefined }>`
  position: relative;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  // top has different
  padding-top: ${theme.spacing.e};
  padding-right: ${theme.spacing.j};
  padding-left: ${theme.spacing.j};
  padding-bottom: ${theme.spacing.j};
  max-height: ${({ maxHeight }) => maxHeight};

  @media (min-width: ${theme.breakpoint.small}) {
    min-width: ${({ width }) => width};
  }
  max-width: 95vw;
`;

const Body = styled.div`
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: ${theme.spacing.i};
`;

const HeaderText = styled.div<{ align: ModalAlign }>`
  text-align: ${({ align }) => align};
`;

const Buttons = styled.div<{ vertical: boolean; align: ModalAlign }>`
  display: grid;
  grid-auto-flow: ${({ vertical }) => (vertical ? 'row' : 'column')};
  justify-content: ${({ align }) => (align === 'center' ? 'center' : align === 'right' ? 'end' : 'start')};
  grid-gap: ${theme.spacing.f};
  margin-top: ${({ vertical }) => (vertical ? theme.spacing.f : 0)};
  grid-template-columns: ${({ vertical }) => (vertical ? '2fr' : '')};
`;

const ButtonsContainer = styled.div``;

const FooterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const CloseContainer = styled.div`
  position: absolute;
  right: ${theme.spacing.f};
  top: ${theme.spacing.f};
`;

const formatCssSizeValue = (value: number | string | undefined, fallback?: string) => {
  if (typeof value === 'number') return `${value}px`;
  if (typeof value === 'string') return /^\d+$/.test(value) ? `${value}px` : value;
  return fallback;
};
